import { ILabel, INode, IRenderContext, LabelStyleBase, Size, SvgVisual, SvgVisualGroup } from "@yfiles/yfiles"
import { MAX_QUEUE_FOR_COLOR } from "./temporal-model"

declare type Cache = SvgVisualGroup & {
  cache?: {
    value: number
    waits: boolean
  }
}

const svgns = 'http://www.w3.org/2000/svg'

// the gauge is a badge of the same size on every node, on its top right corner, so it doesn't cover the node's name
const RADIUS = 16
const THICKNESS = 4

// a step where items wait has a badge of its own: how many wait there, in a quiet colour with a dashed ring like the
// step's outline, as waiting there is the step doing what it does rather than work backing up
const WAIT_TEXT = 'rgb(91,97,114)'
const WAIT_RING = 'rgb(150,160,180)'

/**
 * Returns the color associated with the given intensity value from blue (low) to red (high).
 */
function getIntensityColor(value: number): string {

    const c = `rgb(${(16 + value * 239) | 0}, ${((1 - value) * 239) | 16}, 16)`
    return c
}

// how many items wait at a step in the time simulation, as a gauge that fills and reddens as the queue grows, or at a
// step where items wait, how many are waiting there. Nothing is drawn while none wait
export class TemporalLabelStyleDecorator extends LabelStyleBase {
    protected override getPreferredSize(label: ILabel): Size {
        return new Size(2*RADIUS, 2*RADIUS)
    }

    constructor() {
        super()
    }

    getHeatValue(queue:number): number {
        return Math.floor(Math.min(1, queue/MAX_QUEUE_FOR_COLOR) * 30) / 30
    }

    createVisual(renderContext: IRenderContext, label: ILabel): SvgVisual {
        const group = new SvgVisualGroup() as Cache
        const g = document.createElementNS(svgns, 'g')

        const circleBackground = document.createElementNS(svgns, 'circle')
        circleBackground.r.baseVal.value = RADIUS + 2
        circleBackground.setAttribute('fill', 'white')
        circleBackground.setAttribute('stroke', 'rgb(200,200,200)')
        g.appendChild(circleBackground)

        const trackBackground = document.createElementNS(svgns, 'circle')
        trackBackground.r.baseVal.value = RADIUS - THICKNESS/2
        trackBackground.setAttribute('fill', 'none')
        trackBackground.setAttribute('stroke-width', String(THICKNESS))
        trackBackground.setAttribute('stroke', 'rgb(230,230,230)')
        g.appendChild(trackBackground)

        // starts at the top and goes clockwise
        const circle = document.createElementNS(svgns, 'circle')
        circle.r.baseVal.value = RADIUS - THICKNESS/2
        circle.setAttribute('fill', 'none')
        circle.setAttribute('stroke-width', String(THICKNESS))
        circle.setAttribute('transform', 'rotate(-90)')
        g.appendChild(circle)

        const v = document.createElementNS(svgns, 'text')
        v.setAttribute('text-anchor', 'middle')
        v.setAttribute('dominant-baseline', 'central')
        v.setAttribute('font-weight', '600')
        g.appendChild(v)

        group.add(new SvgVisual(g))
        group.cache = { value: -1, waits: false }
        this.update(g, label, group.cache)
        return group
    }

    override updateVisual(renderContext: IRenderContext, oldVisual: Cache, label: ILabel): SvgVisual {
        const g = oldVisual.children.last()!.svgElement as SVGGElement
        this.update(g, label, oldVisual.cache!)
        return oldVisual as SvgVisual
    }

    private update(g: SVGGElement, label: ILabel, cache: { value: number, waits: boolean }) {
        const node: INode = label.owner as INode
        const { x, y, width } = node.layout
        const temporalNode = node.tag?.temporalNode
        const waits = !!temporalNode?.waits
        const queue = Math.round((waits ? temporalNode.waiting : temporalNode?.queue) || 0)

        if (cache.value !== queue || cache.waits !== waits) {
            const value = this.getHeatValue(queue)
            const color = waits ? WAIT_TEXT : getIntensityColor(value)
            const perimeter = (RADIUS - THICKNESS/2) * 2 * Math.PI

            const circle = g.childNodes.item(2) as SVGCircleElement
            circle.setAttribute('stroke', waits ? WAIT_RING : color)
            circle.setAttribute('stroke-dasharray', waits ? '3 3' : String(perimeter))
            circle.setAttribute('stroke-dashoffset', waits ? '0' : String(perimeter * (1 - value)))

            const text = g.lastElementChild as SVGTextElement
            text.setAttribute('fill', color)
            text.setAttribute('font-size', queue > 999 ? '9' : '11')
            text.textContent = queue > 9999 ? `${Math.round(queue/1000)}K` : String(queue)

            g.style.setProperty('visibility', queue > 0 ? 'visible' : 'hidden')
            cache.value = queue
            cache.waits = waits
        }

        SvgVisual.setTranslate(g, x + width, y)
    }
}
